import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ClockPlus, LoaderCircle } from "lucide-react";
import { useRef, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import { useDialogState } from "@/shared/lib/useDialogState";
import { shiftIso, todayIso } from "@/shared/utils/date";
import { overviewQueryOptions } from "./api/attendance.queries";
import { defaultWindowStart, WINDOW_STEP } from "./attendance.config";
import type { AbsenceDateRange } from "./attendance.types";
import { AbsenceDialog } from "./components/AbsenceDialog";
import { AttendanceGrid } from "./components/AttendanceGrid";
import { WindowNav } from "./components/WindowNav";
import { useVisibleDayCount } from "./useVisibleDayCount";

export function OverviewPage({ canManageRaids }: { canManageRaids: boolean }) {
  const [startIso, setStartIso] = useState(defaultWindowStart);
  // Shared create-absence dialog: toolbar (today) and own-row selection. openWith carries the date range.
  const createAbsence = useDialogState<AbsenceDateRange>();
  // Grid spans as many days as fit; resizing re-slices columns, never refetches.
  const containerRef = useRef<HTMLDivElement>(null);
  const visibleDays = useVisibleDayCount(containerRef);
  // keepPreviousData (not useSuspenseQuery) keeps the old grid on screen while switching windows.
  const { data, isPlaceholderData } = useQuery({
    ...overviewQueryOptions(startIso),
    placeholderData: keepPreviousData,
    // Surface fetch errors on the router error boundary.
    throwOnError: true,
  });

  return (
    <div ref={containerRef} className="space-y-4">
      <div className="flex items-center gap-4">
        <Button
          onClick={() => {
            const today = todayIso();
            createAbsence.openWith({ startDate: today, endDate: today });
          }}
        >
          <ClockPlus />
          Ny frånvaro
        </Button>
        <WindowNav
          startIso={startIso}
          days={visibleDays}
          onPrev={() => setStartIso((s) => shiftIso(s, -WINDOW_STEP))}
          onNext={() => setStartIso((s) => shiftIso(s, WINDOW_STEP))}
          onToday={() => setStartIso(defaultWindowStart())}
        />
      </div>
      {data && (
        <div className="relative">
          <AttendanceGrid
            overview={data}
            startIso={startIso}
            days={visibleDays}
            canManageRaids={canManageRaids}
            onCreateOwn={createAbsence.openWith}
          />
          {isPlaceholderData && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/60">
              <LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      )}
      {/* Kept mounted for Radix close animation; re-seeds on open. */}
      <AbsenceDialog
        open={createAbsence.open}
        onOpenChange={createAbsence.setOpen}
        defaultRange={createAbsence.target ?? undefined}
      />
    </div>
  );
}
